import React, { useMemo, useState, useRef, useEffect } from "react";
import { II } from "./Icons";
import api from "../../services/api";
import { formatDateOnly } from "../../utils/helpers";

/* ─── helpers ─────────────────────────────────────────────────── */

const _FIELD_LABELS = {
  id:"ID", emp_id:"Employee ID", intern_id:"Intern ID", name:"Name",
  full_name:"Full Name", email:"Email", role:"Role", status:"Status",
  phone:"Phone", department:"Department", designation:"Designation",
  joining_date:"Joining Date", date_of_joining:"Joining Date",
  created_at:"Created At", updated_at:"Updated At",
  manager_name:"Manager", manager_id:"Manager ID",
  team:"Team", blood_group:"Blood Group",
  permanent_address:"Address", permanent_city:"City",
  permanent_state:"State", permanent_pincode:"Pincode",
  college:"College", dept:"Department", role_title:"Role Title",
  start_date:"Start Date", end_date:"End Date",
  verified:"Verified", user_id:"User ID",
};
function _label(key) {
  return _FIELD_LABELS[key] ||
    String(key).replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
}
function _fmt(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    try {
      return new Date(s).toLocaleString("en-IN",{
        day:"2-digit", month:"short", year:"numeric",
        hour:"2-digit", minute:"2-digit"
      });
    } catch { return s; }
  }
  return s;
}

function buildDetailCsvRows(detail, mode = "hr") {
  const esc = (v) => '"' + String(v ?? "").replace(/"/g,'""') + '"';
  const lines = [];
  const p = detail?.person || {};

  const secLine = (title) =>
    lines.push(esc("--- " + title.toUpperCase() + " ---") + "," + esc(""));
  const row2 = (a, b) => lines.push(esc(a) + "," + esc(b));
  const row3 = (a, b, c) => lines.push(esc(a) + "," + esc(b) + "," + esc(c));

  lines.push(esc("Field") + "," + esc("Value"));

  // 1. Basic Info
  secLine("Basic Information");
  [
    ["Employee ID",  p.emp_id || p.intern_id],
    ["Name",         p.name || p.full_name],
    ["Email",        p.email],
    ["Role",         p.role],
    ["Status",       p.status],
    ["Phone",        p.phone],
    ["Department",   p.department],
    ["Designation",  p.designation],
    ["Team",         p.team],
    ["Blood Group",  p.blood_group],
    ["Joining Date", p.joining_date || p.date_of_joining],
    ["Created At",   p.created_at],
    ["Manager",      p.manager_name],
    ["Address",      p.permanent_address],
    ["City",         p.permanent_city],
    ["State",        p.permanent_state],
    ["Pincode",      p.permanent_pincode],
  ].forEach(([label, val]) => {
    if (val !== null && val !== undefined && String(val).trim() !== "")
      row2(label, _fmt(val));
  });

  // 2. Onboarding
  const ob = detail?.onboarding;
  if (ob) {
    secLine("Onboarding");
    if (ob.status)       row2("Onboarding Status", ob.status);
    if (ob.submitted_at) row2("Submitted At",       _fmt(ob.submitted_at));
    const sd = ob.step_data;
    if (sd && typeof sd === "object") {
      Object.entries(sd).forEach(([section, sval]) => {
        if (!sval || typeof sval !== "object") return;
        const sLabel = section.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
        secLine(sLabel);
        Object.entries(sval).forEach(([k, v]) => {
          if (v !== null && v !== undefined && String(v).trim() !== "")
            row2(_label(k), _fmt(v));
        });
      });
    }
  }

  // 3. HR / Intern Record
  const hr = detail?.hr_record;
  if (hr && typeof hr === "object" && Object.keys(hr).length > 0) {
    const hrTitle = mode === "hr" && String(p.role||"").toLowerCase() === "intern"
      ? "Intern Record" : "HR Record";
    secLine(hrTitle);
    const skipKeys = new Set(["id","user_id","emp_id","intern_id","name"]);
    Object.entries(hr).forEach(([k, v]) => {
      if (skipKeys.has(k)) return;
      if (v !== null && v !== undefined && String(v).trim() !== "")
        row2(_label(k), _fmt(v));
    });
  }

  // 4. Checklist
  const cl = detail?.checklist || [];
  if (cl.length > 0) {
    secLine("Onboarding Checklist");
    cl.forEach((c) => {
      row2(c.label || c.item || "Item", c.done ? "Completed" : "Pending");
    });
  }

  // 5. Documents
  const docs = detail?.documents || [];
  if (docs.length > 0) {
    secLine("Uploaded Documents");
    row3("Document Name", "Status", "Uploaded At");
    docs.forEach((d) => {
      row3(
        d.doc_name || d.doc_type || d.name || "Document",
        d.status || "Available",
        _fmt(d.uploaded_at || d.created_at || "")
      );
    });
  }

  // 6. Activity Log
  const updates = detail?.updates || [];
  if (updates.length > 0) {
    secLine("Activity Log");
    row3("Action", "Detail", "Date");
    updates.forEach((u) => {
      row3(u.action || "-", u.detail || "-", _fmt(u.created_at || ""));
    });
  }

  return lines.join("\n");
}

function buildBulkCsvRows(people) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const headers = ["Name","Email","Department","Role","Type","Status","Designation","Team","Manager","Joining Date"];
  const typeLabel = (p) => (String(p.role||"").toLowerCase()==="intern" ? "Intern" : "Employee");
  const lines = [
    headers.join(","),
    ...people.map((p) =>
      [p.name||"", p.email||"", p.department||"",
       p.role||"", typeLabel(p), p.status||"",
       p.designation||"", p.team||"",
       p.manager_name||"", p.joining_date ? formatDateOnly(p.joining_date) : ""
      ].map(esc).join(",")
    )
  ];
  return lines.join("\n");
}

function downloadCsvFile(filename, csvString) {
  // csvString is now a plain CSV string (from buildDetailCsvRows)
  const blob = new Blob(["﻿" + csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function downloadBulkCsv(people, suffix) {
  const csv = buildBulkCsvRows(people);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `people_${suffix || "all"}.csv`;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function asList(obj) {
  if (!obj || typeof obj !== "object") return [];
  const isIdKey = (k) => /^(id|emp_id|empid|employee_id|manager_id|project_id|ticket_id|req_id|rid)$/i.test(String(k || ""));
  return Object.entries(obj).filter(([k, v]) =>
    !isIdKey(k) && v !== null && v !== undefined && String(v).trim() !== ""
  );
}

function kvRows(entries) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10, borderBottom: "1px dashed #e2e8f0", paddingBottom: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", textTransform: "capitalize" }}>{String(k).replace(/_/g, " ")}</span>
          <span style={{ fontSize: 12, color: "#0f172a", fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

function sectionCard(title, content) {
  return (
    <div className="cd" style={{ margin: 0 }}>
      <div className="cd-h"><div className="cd-t">{title}</div></div>
      <div className="cd-b">{content}</div>
    </div>
  );
}

function toArray(val) {
  if (Array.isArray(val)) return val.map((v) => String(v).trim()).filter(Boolean);
  if (typeof val === "string") return val.split(",").map((v) => v.trim()).filter(Boolean);
  return [];
}

function unique(values) {
  return Array.from(new Set((values || []).filter(Boolean)));
}

function getPmViewData(detail) {
  const p = detail?.person || {};
  const step = detail?.onboarding?.step_data || {};
  const hr = detail?.hr_record || {};
  const basic = {
    name: p.name || p.full_name, emp_id: p.emp_id, role: p.role, status: p.status,
    department: p.department, designation: p.designation || hr.role || hr.role_title,
    manager: p.manager_name, email: p.email, phone: p.phone,
    joining_date: p.joining_date || hr.joined || hr.start_date
  };
  const skills = unique([
    ...toArray(detail?.skills), ...toArray(step.skills), ...toArray(step.technical_skills),
    ...toArray(step.tech_skills), ...toArray(step.primary_skills),
    ...toArray(p.skills), ...toArray(hr.skills), ...toArray(hr.tech_skills)
  ]);
  const experience = {
    total_experience: detail?.experience?.total_experience || step.total_experience || step.experience || hr.experience || hr.experience_years || "",
    experience_months: detail?.experience?.experience_months || "",
    joining_date: detail?.experience?.joining_date || p.joining_date || "",
    role_experience: step.role_experience || "", education: step.education || step.qualification || hr.education || "",
    certifications: step.certifications || "",
    current_projects_count: detail?.experience?.current_projects_count || "",
    completed_projects_count: detail?.experience?.completed_projects_count || ""
  };
  const projectList = Array.isArray(detail?.projects) ? detail.projects : [];
  const projectHints = unique([...projectList.map((pr) => pr?.name).filter(Boolean), step.project_name, step.project, step.current_project, hr.project, hr.project_name]);
  return { basic, skills, experience, projectHints, projectList };
}

/* ─── fmtJoinedMonth: "2025-03-01" → "Mar 2025" ──────────────── */
function fmtJoinedMonth(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

/* ─── EmpDropdown ─────────────────────────────────────────────── */
function EmpDropdown({ people, selectedId, onChange }) {
  const [query,   setQuery]   = useState("");
  const [open,    setOpen]    = useState(false);
  const [hovered, setHovered] = useState(null);
  const wrapRef = useRef(null);

  const selected = people.find((p) => p.id === selectedId) || null;

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people.slice(0, 60);
    return people.filter((p) =>
      (p.name||"").toLowerCase().includes(q) ||
      (p.department||"").toLowerCase().includes(q) ||
      (p.role||"").toLowerCase().includes(q)
    ).slice(0, 60);
  }, [people, query]);

  const select = (p) => { setQuery(""); setOpen(false); onChange(p ? p.id : ""); };
  const handleInput = (e) => { setQuery(e.target.value); onChange(""); setOpen(true); };

  const typeColor = (p) => String(p.role||"").toLowerCase()==="intern"
    ? { bg:"#eff6ff", cl:"#2563eb" } : { bg:"#eef2ff", cl:"#4f46e5" };
  const statusColor = (p) => {
    const s = String(p.status||"").toLowerCase();
    return s.includes("active") ? { bg:"#dcfce7", cl:"#16a34a" }
         : s.includes("exit")   ? { bg:"#fef2f2", cl:"#ef4444" }
         :                        { bg:"#f3f4f6", cl:"#64748b" };
  };

  return (
    <div ref={wrapRef} style={{ position:"relative", flex:1, minWidth:220 }}>
      <div style={{
        display:"flex", alignItems:"center",
        border: open ? "1.5px solid #6366f1" : "1px solid #e2e8f0",
        borderRadius:8, background:"#fff", padding:"0 10px", gap:6,
        transition:"border-color .15s",
      }}>
        <span style={{ fontSize:13, color:"#94a3b8" }}>🔍</span>
        <input
          value={selected ? `${selected.name}` : query}
          onChange={handleInput}
          onFocus={() => setOpen(true)}
          placeholder="Search & select employee…"
          style={{
            flex:1, border:"none", outline:"none", fontSize:12, padding:"8px 0",
            color: selected ? "#0f172a" : "#374151",
            fontWeight: selected ? 600 : 400, background:"transparent",
          }}
        />
        {(selected || query) && (
          <button onClick={() => { select(null); setQuery(""); }}
            style={{ border:"none", background:"none", cursor:"pointer", color:"#94a3b8", fontSize:14, padding:"0 2px", lineHeight:1 }}>
            ✕
          </button>
        )}
        <span style={{ fontSize:10, color:"#94a3b8", userSelect:"none" }}>▾</span>
      </div>

      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 4px)", left:0, right:0,
          background:"#fff", border:"1px solid #e2e8f0", borderRadius:10,
          boxShadow:"0 8px 24px rgba(0,0,0,.12)", zIndex:999,
          maxHeight:320, overflowY:"auto",
        }}>
          {suggestions.length === 0
            ? <div style={{ padding:16, textAlign:"center", fontSize:12, color:"#94a3b8" }}>No employees found</div>
            : <>
              <div
                onClick={() => select(null)}
                onMouseEnter={() => setHovered("__all")}
                onMouseLeave={() => setHovered(null)}
                style={{
                  padding:"8px 14px", cursor:"pointer", fontSize:12, fontWeight:600, color:"#6366f1",
                  background: hovered==="__all" ? "#f5f3ff" : "#fafafa",
                  borderBottom:"1px solid #e2e8f0",
                }}>
                👥 Show all employees
              </div>
              {suggestions.map((p) => {
                const tc = typeColor(p);
                const sc = statusColor(p);
                const isSel = p.id === selectedId;
                return (
                  <div
                    key={p.id}
                    onClick={() => select(p)}
                    onMouseEnter={() => setHovered(p.id)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      display:"flex", alignItems:"center", gap:10,
                      padding:"9px 14px", cursor:"pointer",
                      background: isSel ? "#f5f3ff" : hovered===p.id ? "#f8fafc" : "#fff",
                      borderBottom:"1px solid #f1f5f9", transition:"background .1s",
                    }}>
                    <div style={{
                      width:30, height:30, borderRadius:"50%",
                      background: isSel ? "#6366f1" : "#e0e7ff",
                      color: isSel ? "#fff" : "#4f46e5",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:12, fontWeight:700, flexShrink:0,
                    }}>
                      {(p.name||"?")[0].toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:"#0f172a",
                        whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize:10, color:"#64748b", marginTop:1 }}>
                        {p.department||"—"}
                        {p.joining_date ? `  ·  Joined ${fmtJoinedMonth(p.joining_date)}` : ""}
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                      <span style={{ padding:"1px 7px", borderRadius:10, fontSize:9, fontWeight:700, background:tc.bg, color:tc.cl }}>
                        {String(p.role||"").toLowerCase()==="intern" ? "Intern" : "Employee"}
                      </span>
                      <span style={{ padding:"1px 7px", borderRadius:10, fontSize:9, fontWeight:700, background:sc.bg, color:sc.cl }}>
                        {p.status||"—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </>
          }
        </div>
      )}
    </div>
  );
}

/* ─── Shared select style ────────────────────────────────────── */
const SS = {
  padding:"7px 12px", border:"1px solid #e2e8f0", borderRadius:8,
  fontSize:12, color:"#374151", background:"#fff", cursor:"pointer", outline:"none",
};

/* ─── Main Component ─────────────────────────────────────────── */
export default function PeopleDirectoryView({
  title = "People Directory",
  people = [],
  loading = false,
  onRefresh,
  onFetchDetail,
  viewMode = "hr"
}) {
  const [selectedId,    setSelectedId]    = useState("");
  const [filterDept,    setFilterDept]    = useState("all");
  const [filterRole,    setFilterRole]    = useState("all");
  const [filterStatus,  setFilterStatus]  = useState("all");
  const [filterMonth,   setFilterMonth]   = useState("all");
  const [active,        setActive]        = useState(null);
  const [activeDetail,  setActiveDetail]  = useState(null);
  const [activeDoc,     setActiveDoc]     = useState(null);
  const [busyId,        setBusyId]        = useState(null);

  /* dynamic filter options */
  const depts = useMemo(() =>
    [...new Set((people||[]).map(p=>p.department).filter(Boolean))].sort(),
    [people]
  );
  const roles = useMemo(() =>
    [...new Set((people||[]).map(p=>String(p.role||"").toLowerCase()).filter(Boolean))].sort(),
    [people]
  );
  const statuses = useMemo(() =>
    [...new Set((people||[]).map(p=>String(p.status||"").toLowerCase()).filter(Boolean))].sort(),
    [people]
  );
  const months = useMemo(() => {
    const raw = (people||[]).map(p => fmtJoinedMonth(p.joining_date)).filter(Boolean);
    const uniq = [...new Set(raw)];
    return uniq.sort((a,b) => new Date(a) - new Date(b));
  }, [people]);

  /* filtered list */
  const filtered = useMemo(() => {
    return (people||[]).filter((p) => {
      if (selectedId && p.id !== selectedId) return false;
      if (filterDept   !=="all" && (p.department||"") !== filterDept) return false;
      if (filterRole   !=="all" && String(p.role||"").toLowerCase() !== filterRole) return false;
      if (filterStatus !=="all" && String(p.status||"").toLowerCase() !== filterStatus) return false;
      if (filterMonth  !=="all" && fmtJoinedMonth(p.joining_date) !== filterMonth) return false;
      return true;
    });
  }, [people, selectedId, filterDept, filterRole, filterStatus, filterMonth]);

  const anyFilter = !!(selectedId || filterDept!=="all" || filterRole!=="all" || filterStatus!=="all" || filterMonth!=="all");

  const resetAll = () => {
    setSelectedId(""); setFilterDept("all"); setFilterRole("all");
    setFilterStatus("all"); setFilterMonth("all");
  };

  const csvSuffix = () => {
    const parts = [];
    if (filterMonth  !=="all") parts.push(filterMonth.replace(" ","_"));
    if (filterDept   !=="all") parts.push(filterDept.replace(/\s+/g,"_"));
    if (filterRole   !=="all") parts.push(filterRole);
    return parts.join("_") || "all";
  };

  const openDetail = async (p) => {
    try {
      setBusyId(p.id);
      const detail = await onFetchDetail(p.id);
      setActive(p); setActiveDetail(detail || null);
    } finally { setBusyId(null); }
  };

  const exportDetail = async (p) => {
    try {
      setBusyId(p.id);
      const detail = await onFetchDetail(p.id);
      const csvString = buildDetailCsvRows(detail || { person: p }, viewMode);
      const empId = detail?.person?.emp_id || detail?.person?.intern_id || p.emp_id || "";
      const safeName = (p.name || "user").replace(/[^a-zA-Z0-9]/g, "_");
      const today = new Date().toISOString().slice(0,10);
      downloadCsvFile(`${safeName}_${empId}_${today}.csv`, csvString);
    } finally { setBusyId(null); }
  };

  const closeModal = () => { setActive(null); setActiveDetail(null); setActiveDoc(null); };

  const getDocUrl = (doc) => {
    const raw = doc?.file_url || doc?.file_path || doc?.url || "";
    return raw ? api.resolveFileUrl(raw) : "";
  };

  const getDocType = (doc) => {
    const name = String(doc?.doc_name||"").toLowerCase();
    const mime = String(doc?.mime_type||"").toLowerCase();
    const url  = String(getDocUrl(doc)||"").toLowerCase();
    if (mime.includes("pdf")||name.endsWith(".pdf")||url.endsWith(".pdf")) return "pdf";
    if (mime.startsWith("image/")||/\.(png|jpg|jpeg|webp|gif)$/i.test(name)||/\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(url)) return "image";
    return "file";
  };

  const getTypeLabel = (p) => String(p.role||"").toLowerCase()==="intern" ? "Intern" : "Employee";

  const typeChipStyle = (p) => {
    const t = getTypeLabel(p);
    return { padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700,
      background: t==="Employee" ? "#eef2ff" : "#eff6ff",
      color:       t==="Employee" ? "#4f46e5" : "#2563eb" };
  };

  const statusChipStyle = (status) => {
    const s = String(status||"").toLowerCase();
    return { padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700,
      background: s.includes("active") ? "#eff6ff" : s.includes("temp")||s.includes("onboarding") ? "#f3f4f6" : "#fef2f2",
      color:       s.includes("active") ? "#2563eb" : s.includes("temp")||s.includes("onboarding") ? "#64748b" : "#ef4444" };
  };

  const overlayStyle = {
    position:"fixed", inset:0, background:"rgba(15,23,42,.45)",
    backdropFilter:"blur(4px)", zIndex:100,
    display:"flex", alignItems:"center", justifyContent:"center"
  };
  const modalCardStyle = {
    background:"#fff", borderRadius:18,
    boxShadow:"0 24px 60px -16px rgba(15,23,42,.2)",
    width:"94vw", maxHeight:"86vh", overflowY:"auto"
  };
  const modalHeadStyle = {
    padding:"18px 22px 14px", borderBottom:"1px solid #e2e8f0",
    display:"flex", alignItems:"center", justifyContent:"space-between",
    position:"sticky", top:0, background:"#fff", zIndex:2, borderRadius:"18px 18px 0 0"
  };
  const modalTitleStyle = { fontSize:17, fontWeight:700, color:"#1e293b" };
  const modalCloseStyle = {
    width:30, height:30, borderRadius:8, border:"none", background:"#f1f5f9",
    display:"flex", alignItems:"center", justifyContent:"center",
    cursor:"pointer", color:"#64748b"
  };

  return (
    <div>
      <div className="sec">{title}</div>

      {/* ── card wrapper ── */}
      <div className="cd">
        {/* card header */}
        <div className="cd-h" style={{ flexWrap:"wrap", gap:8 }}>
          <div>
            <div className="cd-t">All Personnel</div>
            <div className="cd-s">
              {anyFilter ? `${filtered.length} of ${people.length} people` : `${people.length} people`}
            </div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button className="btn-o" onClick={onRefresh} style={{ fontSize:11 }}>
              {II("refresh",12)} Refresh
            </button>
            <button
              onClick={() => downloadBulkCsv(filtered, csvSuffix())}
              disabled={loading || filtered.length===0}
              style={{
                display:"flex", alignItems:"center", gap:6,
                padding:"7px 16px", borderRadius:8,
                background: filtered.length===0 ? "#f1f5f9" : "#0f172a",
                color:       filtered.length===0 ? "#94a3b8" : "#fff",
                border:"none", fontSize:12, fontWeight:700,
                cursor: filtered.length===0 ? "not-allowed" : "pointer",
              }}>
              ⬇&nbsp;Download CSV
            </button>
          </div>
        </div>

        {/* ── filter toolbar ── */}
        <div style={{
          padding:"10px 16px", borderBottom:"1px solid #e2e8f0",
          display:"flex", flexWrap:"wrap", gap:8, alignItems:"center",
          background:"#f8fafc",
        }}>
          {/* employee autocomplete */}
          <EmpDropdown people={people||[]} selectedId={selectedId} onChange={setSelectedId} />

          {/* department */}
          <select value={filterDept} onChange={(e)=>setFilterDept(e.target.value)} style={SS}>
            <option value="all">All Departments</option>
            {depts.map((d)=><option key={d} value={d}>{d}</option>)}
          </select>

          {/* role */}
          <select value={filterRole} onChange={(e)=>setFilterRole(e.target.value)} style={SS}>
            <option value="all">All Roles</option>
            {roles.map((r)=><option key={r} value={r}>{r.toUpperCase()}</option>)}
          </select>

          {/* status */}
          <select value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)} style={SS}>
            <option value="all">All Status</option>
            {statuses.map((s)=><option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>

          {/* joined month */}
          <select value={filterMonth} onChange={(e)=>setFilterMonth(e.target.value)} style={SS}>
            <option value="all">All Months (Joined)</option>
            {months.map((m)=><option key={m} value={m}>{m}</option>)}
          </select>

          {/* reset */}
          {anyFilter && (
            <button onClick={resetAll} style={{
              padding:"7px 12px", border:"1px solid #fecaca",
              borderRadius:8, background:"#fef2f2", color:"#ef4444",
              fontSize:11, fontWeight:700, cursor:"pointer",
            }}>
              ✕ Reset
            </button>
          )}
        </div>

        {/* ── table ── */}
        <div className="cd-b" style={{ padding:0, overflowX:"auto" }}>
          {loading
            ? <div style={{ padding:20, color:"#94a3b8" }}>Loading...</div>
            : filtered.length===0
            ? <div style={{ padding:20, color:"#94a3b8" }}>
                {anyFilter ? "No results match your filters." : "No users found"}
              </div>
            : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Dept</th><th>Role</th>
                    <th>Type</th><th>Status</th><th>Joined</th><th>Profile</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id}>
                      <td style={{ fontWeight:600 }}>{p.name||"-"}</td>
                      <td>{p.department||"-"}</td>
                      <td>{p.role||"-"}</td>
                      <td><span style={typeChipStyle(p)}>{getTypeLabel(p)}</span></td>
                      <td><span style={statusChipStyle(p.status)}>{p.status||"-"}</span></td>
                      <td style={{ fontSize:11, color:"#64748b" }}>
                        {p.joining_date ? fmtJoinedMonth(p.joining_date) : "—"}
                      </td>
                      <td>
                        <button
                          onClick={() => openDetail(p)}
                          disabled={busyId===p.id}
                          style={{ background:"#eef2ff", border:"1px solid #c7d2fe", borderRadius:6,
                            padding:"4px 10px", fontSize:11, fontWeight:600, color:"#4f46e5", cursor:"pointer" }}>
                          {busyId===p.id ? "Loading…" : "View"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      </div>

      {/* ── Detail Modal ── */}
      {active && activeDetail && (
        <div style={overlayStyle} onClick={closeModal}>
          <div style={{ ...modalCardStyle, maxWidth:980 }} onClick={(e)=>e.stopPropagation()}>
            <div style={modalHeadStyle}>
              <div style={modalTitleStyle}>Profile - {active.name}</div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <button
                  onClick={() => exportDetail(active)}
                  disabled={busyId===active.id}
                  style={{ padding:"5px 12px", borderRadius:7, border:"1px solid #e2e8f0",
                    background:"#f8fafc", fontSize:11, fontWeight:600, cursor:"pointer", color:"#374151" }}>
                  ⬇ Export CSV
                </button>
                <button style={modalCloseStyle} onClick={closeModal}>{II("x",18)}</button>
              </div>
            </div>
            <div style={{ padding:"18px 22px 22px", maxHeight:"72vh", overflow:"auto", display:"grid", gap:12 }}>
              {/* hero card */}
              <div style={{ background:"linear-gradient(135deg,#0f172a 0%,#1e1b4b 50%,#312e81 100%)", borderRadius:12, padding:"18px 22px", color:"#fff", position:"relative", overflow:"hidden" }}>
                <div style={{ position:"absolute", top:-30, right:-30, width:140, height:140, background:"radial-gradient(circle,rgba(99,102,241,.15),transparent 70%)", borderRadius:"50%" }}/>
                <div style={{ position:"relative", zIndex:1 }}>
                  <div style={{ fontSize:17, fontWeight:700, marginBottom:2 }}>{activeDetail.person?.name||active.name}</div>
                  <div style={{ fontSize:12, color:"rgba(148,163,184,.8)" }}>{activeDetail.person?.designation||active.designation||"-"} · {activeDetail.person?.department||active.department||"-"}</div>
                  <div style={{ display:"flex", gap:8, marginTop:10 }}>
                    <span style={{ background:"rgba(255,255,255,.1)", padding:"4px 12px", borderRadius:8, fontSize:11, fontWeight:600 }}>{(activeDetail.person?.role||active.role||"-").toUpperCase()} · {activeDetail.person?.status||active.status||"-"}</span>
                  </div>
                </div>
              </div>

              {viewMode==="pm" ? (() => {
                const pmData = getPmViewData(activeDetail);
                return (
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:12 }}>
                    {sectionCard("Basic Details", asList(pmData.basic).length ? kvRows(asList(pmData.basic)) : <div style={{ fontSize:12, color:"#94a3b8" }}>No basic details</div>)}
                    {sectionCard("Skills", pmData.skills.length ? (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {pmData.skills.map((s)=><span key={s} style={{ background:"#eef2ff", color:"#4338ca", padding:"3px 8px", borderRadius:8, fontSize:11, fontWeight:700 }}>{s}</span>)}
                      </div>
                    ) : <div style={{ fontSize:12, color:"#94a3b8" }}>No skills data</div>)}
                    {sectionCard("Experience", asList(pmData.experience).length ? kvRows(asList(pmData.experience)) : <div style={{ fontSize:12, color:"#94a3b8" }}>No experience data</div>)}
                    {sectionCard("Projects", pmData.projectList.length ? (
                      <div style={{ display:"grid", gap:6 }}>
                        {pmData.projectList.map((pr)=>(
                          <div key={`${pr.project_id}-${pr.name}`} style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"6px 8px", fontSize:12 }}>
                            <b>{pr.name||"-"}</b>
                            <div style={{ color:"#334155", fontSize:11 }}>{pr.member_role||"-"} | {pr.status||"-"}</div>
                            <div style={{ color:"#94a3b8", fontSize:10 }}>Start: {formatDateOnly(pr.start_date)} | End: {formatDateOnly(pr.deadline)}</div>
                          </div>
                        ))}
                      </div>
                    ) : pmData.projectHints.length ? (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {pmData.projectHints.map((p)=><span key={p} style={{ background:"#f1f5f9", color:"#334155", padding:"3px 8px", borderRadius:8, fontSize:11, fontWeight:700 }}>{p}</span>)}
                      </div>
                    ) : <div style={{ fontSize:12, color:"#94a3b8" }}>No projects found</div>)}
                  </div>
                );
              })() : (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:12 }}>
                  {sectionCard("Basic Details", asList(activeDetail.person).length ? kvRows(asList(activeDetail.person)) : <div style={{ fontSize:12, color:"#94a3b8" }}>No basic details</div>)}

                  {sectionCard("Onboarding", !activeDetail.onboarding ? <div style={{ fontSize:12, color:"#94a3b8" }}>No onboarding record</div> : (
                    <div style={{ display:"grid", gap:10 }}>
                      <div>{kvRows(asList({ status:activeDetail.onboarding.status, submitted_at:activeDetail.onboarding.submitted_at }))}</div>
                      <div style={{ borderTop:"1px solid #e2e8f0", paddingTop:8 }}>{kvRows(asList(activeDetail.onboarding.step_data))}</div>
                    </div>
                  ))}

                  {sectionCard("Checklist", !activeDetail.checklist?.length ? <div style={{ fontSize:12, color:"#94a3b8" }}>No checklist entries</div> : (
                    <div style={{ display:"grid", gap:6 }}>
                      {activeDetail.checklist.map((c,i)=>(
                        <div key={i} style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"6px 8px", fontSize:12 }}>
                          <b>{c.label||"-"}</b>
                          <div style={{ color:"#64748b", fontSize:11 }}>{c.done?"Done":"Pending"} | {formatDateOnly(c.completed_at)}</div>
                        </div>
                      ))}
                    </div>
                  ))}

                  {sectionCard("HR Record", !activeDetail.hr_record ? <div style={{ fontSize:12, color:"#94a3b8" }}>No HR record found</div> : kvRows(asList(activeDetail.hr_record)))}

                  {sectionCard("Documents", !activeDetail.documents?.length ? <div style={{ fontSize:12, color:"#94a3b8" }}>No documents</div> : (
                    <div style={{ display:"grid", gap:6 }}>
                      {activeDetail.documents.map((d)=>(
                        <div key={d.id} style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"6px 8px", fontSize:12 }}>
                          <b>{d.doc_name||d.doc_type||"Document"}</b>
                          <div style={{ color:"#64748b", fontSize:11 }}>{d.status||"pending"} | {formatDateOnly(d.uploaded_at)}</div>
                          <div style={{ marginTop:6 }}>
                            <button className="btn-o btn-s" onClick={()=>setActiveDoc(d)}>{II("eye",12)} View File</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}

                  {sectionCard("Credential/Account Updates", !activeDetail.updates?.length ? <div style={{ fontSize:12, color:"#94a3b8" }}>No update history</div> : (
                    <div style={{ display:"grid", gap:6 }}>
                      {activeDetail.updates.map((u,i)=>(
                        <div key={i} style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"6px 8px", fontSize:12 }}>
                          <b>{u.action}</b>
                          <div style={{ color:"#334155", fontSize:11 }}>{u.detail}</div>
                          <div style={{ color:"#94a3b8", fontSize:10 }}>{formatDateOnly(u.created_at)}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Doc viewer Modal ── */}
      {active && activeDoc && (
        <div style={overlayStyle} onClick={()=>setActiveDoc(null)}>
          <div style={{ ...modalCardStyle, maxWidth:920 }} onClick={(e)=>e.stopPropagation()}>
            <div style={modalHeadStyle}>
              <div style={modalTitleStyle}>File View - {activeDoc.doc_name||activeDoc.doc_type||"Document"}</div>
              <button style={modalCloseStyle} onClick={()=>setActiveDoc(null)}>{II("x",18)}</button>
            </div>
            <div style={{ padding:"18px 22px 22px", display:"grid", gap:12, maxHeight:"72vh", overflow:"auto" }}>
              <div className="cd" style={{ margin:0 }}>
                <div className="cd-b" style={{ display:"grid", gap:6 }}>
                  <div style={{ fontSize:12 }}><b>Type:</b> {activeDoc.doc_type||"-"}</div>
                  <div style={{ fontSize:12 }}><b>Status:</b> {activeDoc.status||"pending"}</div>
                  <div style={{ fontSize:12 }}><b>Uploaded At:</b> {formatDateOnly(activeDoc.uploaded_at)}</div>
                  {activeDoc.review_comment ? <div style={{ fontSize:12 }}><b>Review Comment:</b> {activeDoc.review_comment}</div> : null}
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:4 }}>
                    <button className="btn-o btn-s" onClick={()=>api.previewFile(activeDoc.file_path||activeDoc.file_url, activeDoc.doc_name||"document")}>{II("eye",12)} Open</button>
                    <button className="btn-o btn-s" onClick={()=>api.downloadFile(activeDoc.file_path||activeDoc.file_url, activeDoc.doc_name||"document")}>{II("dwn",12)} Download</button>
                  </div>
                </div>
              </div>
              <div className="cd" style={{ margin:0 }}>
                <div className="cd-h"><div className="cd-t">Preview</div></div>
                <div className="cd-b">
                  {getDocType(activeDoc)==="pdf" && getDocUrl(activeDoc)
                    ? <iframe title="doc-preview" src={getDocUrl(activeDoc)} style={{ width:"100%", minHeight:460, border:"1px solid #e2e8f0", borderRadius:8 }}/>
                    : getDocType(activeDoc)==="image" && getDocUrl(activeDoc)
                    ? <img src={getDocUrl(activeDoc)} alt={activeDoc.doc_name||"document"} style={{ width:"100%", maxHeight:520, objectFit:"contain", border:"1px solid #e2e8f0", borderRadius:8 }}/>
                    : <div style={{ fontSize:12, color:"#64748b" }}>Preview not available for this file type. Use Open or Download.</div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
